import React, { Fragment } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { BreakpointProvider } from "@hsl/core/hooks/useBreakpoint";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import { type Config } from "../../../config/types";
import { useTimestamp } from "../../../hooks/useTimestamp";
import useTranslation, { Translate } from "../../../hooks/useTranslation";
import { type SubscribableAsset } from "../../../openapi";
import { ExplorerStoreContext } from "../../../store/store";
import { GenericErrorBoundary } from "../../GenericErrorBoundary/GenericErrorBoundary";
import Loading from "../../Loading";
import Select from "../../Select";
import { AcceptFunds } from "../AcceptFunds";
import { EmptyState } from "../EmptyState";
import { txStrReplacer } from "../lib";
import { useFundCentres } from "../useFundCentres";
import { SquareFundCard } from "./SquareFundCard";

interface Props {
    collectivesConfig: Config;
    etfConfig: Config;
    pmcConfig: Config;
}

export const MyFunds = ({ collectivesConfig, etfConfig, pmcConfig }: Props) => {
    const { clickLinkEvent } = useYardActionEvents();
    const params = useSearchParamString();
    const user = useFCSubscriptionStore((state) => state.user);
    const loadingUser = useFCSubscriptionStore((state) => state.loadingUser);
    const latestFC = useFCSubscriptionStore((state) => state.latestFC);
    const subscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const sort = useFCSubscriptionStore((state) => state.myFundsSort);
    const filter = useFCSubscriptionStore((state) => state.myFundsFilter);
    const markRead = useFCSubscriptionStore((state) => state.markRead);
    const isUpdated = useFCSubscriptionStore(
        (state) => state.isUpdatedSubAsset,
    );
    const updates = useFCSubscriptionStore((state) => state.updates);
    const key = useTimestamp(updates);

    const T = useTranslation();
    const userFundsTxt = T("[name]'s funds").result;

    const subscribedFunds = Array.from(
        new Set(subscriptions.map((s) => s.fundId)),
    );

    const confirmedSubscriptions = subscriptions.filter(
        (sub) => sub.subscriptionStatus === "CONFIRMED",
    );

    const hasUpdates = subscriptions.some(isUpdated);

    const subscribedAndConfirmedFunds = Array.from(
        new Set(confirmedSubscriptions.map((s) => s.fundId)),
    );

    const fundCentres = useFundCentres({
        collectivesConfig,
        etfConfig,
        pmcConfig,
        subscribedFunds,
    });

    if (loadingUser) {
        return <Loading />;
    }

    if (!user) {
        return <Navigate to={latestFC + params} />;
    }

    const subscribedFundsWithFundCentres = subscribedAndConfirmedFunds.filter(
        (fundId) => {
            const [type, id] = fundId.split("-") as [string, string];
            return fundCentres.some(
                (fc) => fc.data?.type === type && fc.data?.funds.includes(id),
            );
        },
    );

    const handleClearNotifications = (
        e: React.MouseEvent<HTMLButtonElement>,
    ) => {
        e.preventDefault();
        markRead();
    };

    return (
        <BreakpointProvider>
            <main className="w-full px-4 pb-16 pt-8 sm:px-0">
                <AcceptFunds
                    fundCentres={fundCentres}
                    className="mb-8"
                    startOpen
                />
                <div className="flex w-full flex-col justify-between gap-4 md:flex-row md:items-end">
                    <h1 className="pii text-lg font-medium sm:text-3xl sm:font-normal">
                        {txStrReplacer(userFundsTxt, {
                            name: `${user.firstName}`,
                        })}
                    </h1>
                    <Link
                        to={`/my-funds/manage${params}`}
                        className="text-battleship-grey font-medium underline"
                        onClick={() =>
                            clickLinkEvent({
                                name: "Manage subscriptions",
                                position: "My Funds navigation",
                            })
                        }
                    >
                        <Translate>
                            Manage subscriptions and watchlist
                        </Translate>
                    </Link>
                </div>
                <GenericErrorBoundary>
                    {fundCentres.some((fc) => !fc.data && fc.isLoading) ? (
                        <Loading />
                    ) : (
                        <Fragment key={key}>
                            <div className="mt-6 flex w-full flex-col items-center justify-between gap-4 md:flex-row">
                                <div className="flex w-full flex-wrap gap-x-6 gap-y-2.5 md:gap-12">
                                    {subscribedFundsWithFundCentres.length >
                                        1 && (
                                        <>
                                            <FilterDropdown />
                                            <SortDropdown />
                                        </>
                                    )}
                                    {hasUpdates && (
                                        <button
                                            onClick={handleClearNotifications}
                                            className="ml-auto inline-block text-sm underline"
                                        >
                                            <Translate>
                                                Clear all notifications
                                            </Translate>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
                                {subscribedFundsWithFundCentres.length > 0 ? (
                                    subscribedFundsWithFundCentres
                                        .map((fundId) => {
                                            const [type, id] = fundId.split(
                                                "-",
                                            ) as [string, string];
                                            const query = fundCentres.find(
                                                (fc) =>
                                                    fc.data?.type == type &&
                                                    fc.data?.funds.includes(id),
                                            );
                                            return [type, id, query!] as const;
                                        })
                                        .filter(([, , query]) => !!query)
                                        .filter(([type, id]) => {
                                            const fundId = `${type}-${id}`;
                                            switch (filter) {
                                                case "subscriptions": {
                                                    return confirmedSubscriptions.some(
                                                        (sub) =>
                                                            sub.fundId ===
                                                                fundId &&
                                                            sub.subscriptionType ===
                                                                "FULL",
                                                    );
                                                }
                                                case "watchlist": {
                                                    return !confirmedSubscriptions.some(
                                                        (sub) =>
                                                            sub.fundId ===
                                                                fundId &&
                                                            sub.subscriptionType ===
                                                                "FULL",
                                                    );
                                                }
                                                default:
                                                    return true;
                                            }
                                        })
                                        .sort(
                                            (
                                                [aType, aId, aQuery],
                                                [bType, bId, bQuery],
                                            ) => {
                                                switch (sort) {
                                                    case "most recent": {
                                                        return (
                                                            getMostRecentSubscriptionDate(
                                                                confirmedSubscriptions.filter(
                                                                    (x) =>
                                                                        x.fundId ===
                                                                        `${bType}-${bId}`,
                                                                ),
                                                            ) -
                                                            getMostRecentSubscriptionDate(
                                                                confirmedSubscriptions.filter(
                                                                    (x) =>
                                                                        x.fundId ===
                                                                        `${aType}-${aId}`,
                                                                ),
                                                            )
                                                        );
                                                    }
                                                    case "alphabetical": {
                                                        try {
                                                            const aFund = aQuery
                                                                .data!.store.getState()
                                                                .getFundById(
                                                                    `fund-${aId}`,
                                                                );
                                                            const bFund = bQuery
                                                                .data!.store.getState()
                                                                .getFundById(
                                                                    `fund-${bId}`,
                                                                );
                                                            if (!aFund)
                                                                return -1;
                                                            if (!bFund)
                                                                return 1;
                                                            return aFund.data.data.name.localeCompare(
                                                                bFund.data.data
                                                                    .name,
                                                            );
                                                        } catch (err) {
                                                            console.error(err);
                                                            return 0;
                                                        }
                                                    }
                                                    default:
                                                        return 0;
                                                }
                                            },
                                        )
                                        .map(([type, id, query], index) => {
                                            return (
                                                <ExplorerStoreContext.Provider
                                                    value={query.data!.store}
                                                    key={`${type}-${id}`}
                                                >
                                                    <SquareFundCard
                                                        fundId={id}
                                                        index={index}
                                                        subscriptions={confirmedSubscriptions.filter(
                                                            (x) =>
                                                                x.fundId ===
                                                                `${type}-${id}`,
                                                        )}
                                                    />
                                                </ExplorerStoreContext.Provider>
                                            );
                                        })
                                ) : (
                                    <EmptyState />
                                )}
                            </div>
                        </Fragment>
                    )}
                </GenericErrorBoundary>
            </main>
        </BreakpointProvider>
    );
};

// leaving this here in case we want to bring back the toggle

// const SortToggle = () => {
//     const sort = useFCSubscriptionStore((state) => state.myFundsSort);
//     const setSort = useFCSubscriptionStore((state) => state.setMyFundsSort);

//     return (
//         <label className="flex items-center gap-2 text-sm">
//             <input
//                 type="checkbox"
//                 className="hidden"
//                 checked={sort === "alphabetical"}
//                 onChange={(evt) => {
//                     if (evt.target.checked) {
//                         setSort("alphabetical");
//                     } else {
//                         setSort("most recent");
//                     }
//                 }}
//             />
//             <span
//                 className={cx(
//                     "transition-colors duration-200",
//                     sort === "most recent"
//                         ? "text-core-blue"
//                         : "text-battleship-grey",
//                 )}
//             >
//                 <Translate>Most recent</Translate>
//             </span>
//             <div className="border-battleship-grey -mt-px h-4 w-8 rounded-full border bg-white p-0.5">
//                 <div
//                     className={cx(
//                         "bg-core-blue h-2.5 w-2.5 rounded-full transition-transform duration-200",
//                         sort === "alphabetical" && "translate-x-4",
//                     )}
//                 />
//             </div>
//             <span
//                 className={cx(
//                     "transition-colors duration-200",
//                     sort === "alphabetical"
//                         ? "text-core-blue"
//                         : "text-battleship-grey",
//                 )}
//             >
//                 <Translate>A-Z</Translate>
//             </span>
//         </label>
//     );
// };

const FilterDropdown = () => {
    const T = useTranslation();
    const filter = useFCSubscriptionStore((state) => state.myFundsFilter);
    const setFilter = useFCSubscriptionStore((state) => state.setMyFundsFilter);

    return (
        <Select<typeof filter>
            label={`${T("Filter by").result}:`}
            value={filter}
            options={[
                {
                    label: T("All").result,
                    key: "all",
                    value: "all",
                },
                {
                    label: T("Subscriptions").result,
                    key: "subscriptions",
                    value: "subscriptions",
                },
                {
                    label: T("Watchlist").result,
                    key: "watchlist",
                    value: "watchlist",
                },
            ]}
            onChange={(value) => setFilter(value)}
            optionClassName="text-sm border-b hover:font-normal py-2 last:border-b-0 cursor-pointer"
        />
    );
};

const SortDropdown = () => {
    const T = useTranslation();
    const sort = useFCSubscriptionStore((state) => state.myFundsSort);
    const setSort = useFCSubscriptionStore((state) => state.setMyFundsSort);

    return (
        <Select<typeof sort>
            label={`${T("Sort by").result}:`}
            value={sort}
            options={[
                {
                    label: T("Date added").result,
                    key: "most recent",
                    value: "most recent",
                },
                {
                    label: T("A-Z").result,
                    key: "alphabetical",
                    value: "alphabetical",
                },
            ]}
            onChange={(value) => setSort(value)}
            optionClassName="text-sm border-b hover:font-normal py-2 last:border-b-0 cursor-pointer"
        />
    );
};

export function getMostRecentSubscriptionDate(
    subscriptions: Required<SubscribableAsset>[],
) {
    const mostRecentSub = subscriptions.sort(
        (a, b) =>
            new Date(b.createdDateTime).getDate() -
            new Date(a.createdDateTime).getDate(),
    )[0];

    return new Date(mostRecentSub?.createdDateTime ?? 0).getTime();
}

const useSearchParamString = () => {
    const [searchParams] = useSearchParams();
    const string = searchParams.toString();
    if (string) return "?" + string;
    return "";
};
